import { useContext, useMemo, useState } from "react";
import { useQueryClient } from "react-query";
import { useAuth } from "react-oidc-context";
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers-pro";
import moment from "moment";

import Button from "components/Button";
import replacementsService, {
  IReplacement,
  replacementsQueryKeys,
} from "api/replacementsService";
import workspaceService from "api/workspaceService";
import CompanyContext from "contexts/CompanyContext";
import { getUserId } from "utils/getUserId";
import { ENotificationType, notification } from "utils/notification";

import styles from "./styles.module.scss";

// Zastępstwa (port ReplacementViewModel z Magistratu): zarządzający (właściciel /
// workspace.members.edit) ustawia zastępstwo za dowolnego pracownika, pozostali —
// wyłącznie za siebie. Lista bieżących/przyszłych oraz archiwum (zakończone).

const isoDate = (v: moment.Moment | null) =>
  v && v.isValid() ? v.format("YYYY-MM-DD") : "";
const toMoment = (v?: string | null) => (v ? moment(v) : null);
const fmt = (v: string) => moment(v).format("DD.MM.YYYY");

const ReplacementsTab = () => {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const { company } = useContext(CompanyContext);
  const workspaceId = company?.Id ?? "";
  const myId = getUserId(auth.user);

  const [showArchive, setShowArchive] = useState(false);
  const { data: listData, isLoading } = replacementsService.useGetList(
    showArchive,
  );
  const { data: workspaceData } =
    workspaceService.useGetWorkspace(workspaceId);
  const { mutate: save, isLoading: isSaving } = replacementsService.useSave();
  const { mutate: remove } = replacementsService.useRemove();

  const items = listData?.data?.Items ?? [];
  const canManageOthers = listData?.data?.CanManageOthers ?? false;

  // Aktywni członkowie firmy (Status 4 = Active) — kandydaci na zastępowanego/zastępcę.
  const members = useMemo(
    () =>
      (workspaceData?.data?.Members ?? [])
        .filter((m) => m.Status === 4 && !!m.UserId)
        .sort((a, b) => (a.Label ?? "").localeCompare(b.Label ?? "")),
    [workspaceData],
  );

  // Edytor
  const [editingId, setEditingId] = useState<string | null>(null);
  const [forOther, setForOther] = useState(false);
  const [replacedUserId, setReplacedUserId] = useState<string>("");
  const [replacingUserId, setReplacingUserId] = useState<string>("");
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");

  const clearEditor = () => {
    setEditingId(null);
    setForOther(false);
    setReplacedUserId("");
    setReplacingUserId("");
    setStartDate("");
    setEndDate("");
  };

  const startEdit = (r: IReplacement) => {
    setEditingId(r.Id);
    setForOther(r.ReplacedUserId !== myId);
    setReplacedUserId(r.ReplacedUserId);
    setReplacingUserId(r.ReplacingUserId);
    setStartDate(r.StartDate);
    setEndDate(r.EndDate);
  };

  const effectiveReplacedId = forOther ? replacedUserId : myId;
  const today = moment().format("YYYY-MM-DD");
  const canApply =
    !!replacingUserId &&
    replacingUserId !== effectiveReplacedId &&
    (!forOther || !!replacedUserId) &&
    !!startDate &&
    !!endDate &&
    endDate >= startDate &&
    (editingId ? true : startDate >= today);

  const refresh = () =>
    queryClient.invalidateQueries(replacementsQueryKeys.list);

  const handleApply = () => {
    save(
      {
        Id: editingId,
        ReplacedUserId: forOther ? replacedUserId : null,
        ReplacingUserId: replacingUserId,
        StartDate: startDate,
        EndDate: endDate,
      },
      {
        onSuccess: () => {
          clearEditor();
          refresh();
          // Zastępca może od razu zobaczyć nowe przełączenie w popupie konta.
          queryClient.invalidateQueries(replacementsQueryKeys.switches);
        },
        onError: (error) =>
          notification({
            type: ENotificationType.ERROR,
            title: "Zastępstwa",
            text:
              error?.response?.data?.errorCodes?.[0]?.message ??
              "Nie udało się zapisać zastępstwa",
          }),
      },
    );
  };

  const handleRemove = (r: IReplacement) => {
    if (!window.confirm("Czy na pewno chcesz usunąć to zastępstwo?")) return;
    remove(r.Id, {
      onSuccess: () => {
        if (editingId === r.Id) clearEditor();
        refresh();
        queryClient.invalidateQueries(replacementsQueryKeys.switches);
      },
      onError: (error) =>
        notification({
          type: ENotificationType.ERROR,
          title: "Zastępstwa",
          text:
            error?.response?.data?.errorCodes?.[0]?.message ??
            "Nie udało się usunąć zastępstwa",
        }),
    });
  };

  const current = items.filter((r) => !r.IsArchived);
  const archived = items.filter((r) => r.IsArchived);

  const renderRow = (r: IReplacement) => (
    <div
      key={r.Id}
      className={`${styles.row} ${r.IsActive ? styles.rowActive : ""}`}
    >
      <div className={styles.rowMain}>
        <div className={styles.rowTitle}>
          <span className={styles.name}>{r.ReplacedUserLabel ?? "—"}</span>
          <span className={styles.arrow}>← zastępuje</span>
          <span className={styles.name}>{r.ReplacingUserLabel ?? "—"}</span>
        </div>
        <div className={styles.rowMeta}>
          {fmt(r.StartDate)} – {fmt(r.EndDate)}
          {r.IsActive && <span className={styles.badgeActive}>trwa</span>}
          {r.IsArchived && <span className={styles.badge}>zakończone</span>}
          {r.CreatedByLabel && (
            <span className={styles.by}>ustawił(a): {r.CreatedByLabel}</span>
          )}
        </div>
      </div>
      {r.CanEdit && !r.IsArchived && (
        <div className={styles.rowActions}>
          <Button text="Zmień" onClick={() => startEdit(r)} />
          <Button text="Usuń" onClick={() => handleRemove(r)} />
        </div>
      )}
    </div>
  );

  return (
    <div className={styles.wrapper}>
      <div className={styles.section}>
        <div className={styles.sectionTitle}>
          {editingId ? "Zmiana zastępstwa" : "Nowe zastępstwo"}
        </div>
        <div className={styles.hint}>
          W czasie zastępstwa zastępca może przełączyć się na konto zastępowanego
          (popup konta w menu bocznym) i pracować w jego imieniu; utworzone wtedy
          pisma mają w stopce adnotację „w zastępstwie za".
        </div>

        {canManageOthers && (
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={forOther}
                disabled={!!editingId}
                onChange={(e) => {
                  setForOther(e.target.checked);
                  if (!e.target.checked) setReplacedUserId("");
                }}
              />
            }
            label="Ustaw zastępstwo za innego pracownika"
          />
        )}

        <div className={styles.twoCol}>
          <FormControl fullWidth size="small" disabled={!forOther || !!editingId}>
            <InputLabel>Zastępowany</InputLabel>
            <Select
              label="Zastępowany"
              value={forOther ? replacedUserId : myId}
              onChange={(e) => setReplacedUserId(e.target.value as string)}
            >
              {!forOther && (
                <MenuItem value={myId}>
                  {auth.user?.profile?.name} (ja)
                </MenuItem>
              )}
              {forOther &&
                members.map((m) => (
                  <MenuItem key={m.UserId} value={m.UserId}>
                    {m.Label}
                    {m.IsMe ? " (ja)" : ""}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel>Zastępca</InputLabel>
            <Select
              label="Zastępca"
              value={replacingUserId}
              onChange={(e) => setReplacingUserId(e.target.value as string)}
            >
              <MenuItem value="">—</MenuItem>
              {members
                .filter((m) => m.UserId !== effectiveReplacedId)
                .map((m) => (
                  <MenuItem key={m.UserId} value={m.UserId}>
                    {m.Label}
                    {m.IsMe ? " (ja)" : ""}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </div>

        <div className={styles.twoCol}>
          <DatePicker
            label="Od"
            value={toMoment(startDate)}
            minDate={editingId ? undefined : moment()}
            onChange={(v) => setStartDate(isoDate(v))}
            slotProps={{ textField: { size: "small", fullWidth: true } }}
          />
          <DatePicker
            label="Do"
            value={toMoment(endDate)}
            minDate={toMoment(startDate) ?? moment()}
            onChange={(v) => setEndDate(isoDate(v))}
            slotProps={{ textField: { size: "small", fullWidth: true } }}
          />
        </div>

        <div className={styles.actions}>
          <Button
            text={editingId ? "Zapisz" : "Ustaw"}
            disabled={!canApply || isSaving}
            onClick={handleApply}
          />
          {editingId && <Button text="Anuluj" onClick={clearEditor} />}
        </div>
      </div>

      <div className={styles.section}>
        <div className={styles.sectionTitle}>Bieżące i zaplanowane</div>
        {isLoading && <div className={styles.hint}>Ładowanie…</div>}
        {!isLoading && current.length === 0 && (
          <div className={styles.hint}>Brak zastępstw.</div>
        )}
        <div className={styles.list}>{current.map(renderRow)}</div>
      </div>

      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <div className={styles.sectionTitle}>Archiwum</div>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={showArchive}
                onChange={(e) => setShowArchive(e.target.checked)}
              />
            }
            label="Pokaż zakończone"
          />
        </div>
        {showArchive && archived.length === 0 && (
          <div className={styles.hint}>Brak zakończonych zastępstw.</div>
        )}
        {showArchive && <div className={styles.list}>{archived.map(renderRow)}</div>}
      </div>
    </div>
  );
};

export default ReplacementsTab;
